import React, { useMemo, useState } from "react";
import {
  HiCheckCircle,
  HiExclamationCircle,
  HiPencil,
  HiRefresh,
  HiSparkles,
} from "react-icons/hi";
import { MarkdownContent } from "../../Common/MarkdownContent";
import type { AgentEvaluationScenarioResult } from "../../../services/api";
import { InlineProgress } from "./InlineProgress";
import {
  formatPercent,
  getStatusClasses,
  shortId,
  statusLabel,
} from "./shared";
import { UseAgentTestLab } from "./useAgentTestLab";

interface PromptOptimizerPanelProps {
  lab: UseAgentTestLab;
}

export const PromptOptimizerPanel: React.FC<PromptOptimizerPanelProps> = ({
  lab,
}) => {
  const candidate = lab.activeCandidate;

  if (!candidate) {
    return (
      <section className="bg-[var(--card)] border border-[var(--border)] rounded-lg p-8 text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-[var(--accent)]/10 text-[var(--accent)] flex items-center justify-center">
          <HiSparkles className="w-6 h-6" />
        </div>
        <div className="space-y-1">
          <h3 className="text-lg font-medium">Optimizer is idle</h3>
          <p className="text-sm text-[var(--foreground-muted)] max-w-md mx-auto">
            Run a baseline with at least one failing scenario, then optimize. The
            optimizer drafts a candidate prompt and reruns the same scenarios in
            shadow mode for an apples-to-apples comparison.
          </p>
        </div>
        <button
          type="button"
          onClick={lab.handleOptimizePrompt}
          disabled={
            lab.isOptimizing ||
            lab.failedScenarios.length === 0 ||
            !lab.latestBaselineRun
          }
          className="px-3 py-2 text-sm bg-[var(--accent)] text-[var(--accent-foreground)] rounded-md hover:bg-[var(--accent-hover)] disabled:opacity-50 inline-flex items-center gap-2"
        >
          <HiSparkles className="w-4 h-4" />
          {lab.isOptimizing ? "Optimizing…" : "Optimize Prompt from Failures"}
        </button>
        {lab.isOptimizing && (
          <div className="max-w-xl mx-auto text-left">
            <InlineProgress
              label="Creating and testing prompt candidate"
              active={lab.isOptimizing}
              hint={`The optimizer first drafts a candidate, then runs the same failed scenarios in shadow mode. This tab refreshes automatically.`}
              onCancel={lab.cancelOptimization}
            />
          </div>
        )}
        {lab.promptVersions.length > 0 && (
          <div className="pt-4">
            <PromptHistory lab={lab} />
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="bg-[var(--card)] border border-[var(--border)] rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-[var(--border)] flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-[var(--foreground-muted)]">
            Candidate {shortId(candidate.id)} · {candidate.source} · iteration{" "}
            {candidate.iteration_count}
          </div>
          <h3 className="text-lg font-medium mt-0.5">
            Prompt Optimizer
          </h3>
        </div>
        <span
          className={`px-2 py-0.5 rounded-full border text-xs capitalize ${getStatusClasses(
            candidate.status
          )}`}
        >
          {statusLabel(candidate.status)}
        </span>
        <button
          type="button"
          onClick={lab.handleOptimizePrompt}
          disabled={
            lab.isOptimizing ||
            lab.failedScenarios.length === 0 ||
            !lab.latestBaselineRun
          }
          className="px-2.5 py-1.5 text-xs rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)] inline-flex items-center gap-1.5"
        >
          <HiSparkles className="w-4 h-4" />
          {lab.isOptimizing ? "Optimizing…" : "Run optimizer again"}
        </button>
      </div>

      {lab.isOptimizing && (
        <div className="px-5 pt-4">
          <InlineProgress
            label="Optimizing prompt"
            active={lab.isOptimizing}
            hint={`Up to ${lab.maxIterations} iterations · running scenarios in shadow mode. Cancelling discards the result.`}
            onCancel={lab.cancelOptimization}
          />
        </div>
      )}

      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1fr)_420px] xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="p-5 space-y-5 border-b xl:border-b-0 xl:border-r border-[var(--border)]">
          <AccuracyDiffBars lab={lab} />

          {candidate.risk_notes && (
            <div className="flex items-start gap-2 text-sm bg-amber-50 text-amber-900 border-l-2 border-amber-400 rounded-r-md px-3 py-2">
              <HiExclamationCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
              <div className="min-w-0">
                <div className="font-medium">Risk notes from optimizer</div>
                <MarkdownContent className="mt-1 [&_*]:text-amber-900">
                  {candidate.risk_notes}
                </MarkdownContent>
              </div>
            </div>
          )}

          <PromptDiff lab={lab} />

          <ImpactGroups lab={lab} />

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              type="button"
              onClick={() =>
                lab.setPromptEditor({
                  mode: "candidate",
                  prompt: candidate.proposed_prompt,
                  candidateId: candidate.id,
                })
              }
              disabled={lab.promptActionInFlight}
              className="px-3 py-1.5 text-sm rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <HiPencil className="w-4 h-4" />
              Edit proposed
            </button>
            <button
              type="button"
              onClick={() => lab.handleRerunCandidate(candidate)}
              disabled={lab.promptActionInFlight}
              className="px-3 py-1.5 text-sm rounded-md hover:bg-[var(--muted)] text-[var(--foreground-muted)] disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <HiRefresh className="w-4 h-4" />
              Rerun
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => lab.handleAcceptCandidate(candidate)}
              disabled={!lab.canAcceptCandidate || lab.promptActionInFlight}
              className="px-4 py-1.5 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <HiCheckCircle className="w-4 h-4" />
              Accept new prompt
            </button>
          </div>
        </div>

        <div className="p-5 bg-[var(--card-container)]">
          <PromptHistory lab={lab} />
        </div>
      </div>
    </section>
  );
};

const AccuracyDiffBars: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => {
  const candidate = lab.activeCandidate!;
  const baseline = candidate.baseline_accuracy ?? 0;
  const candAcc = candidate.candidate_accuracy ?? 0;
  const delta = candAcc - baseline;
  const better = delta > 0;

  const barFor = (value: number, color: string, label: string) => (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-[var(--foreground-muted)]">{label}</span>
        <span className="font-semibold tabular-nums">
          {formatPercent(value)}
        </span>
      </div>
      <div className="h-2 w-full bg-[var(--muted)] rounded-full overflow-hidden">
        <div
          className={`h-full ${color} transition-all`}
          style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }}
        />
      </div>
    </div>
  );

  return (
    <div className="rounded-lg border border-[var(--border)] bg-[var(--card-container)] p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">Accuracy comparison</div>
        <span
          className={`text-xs font-semibold tabular-nums ${
            better
              ? "text-green-600"
              : delta < 0
                ? "text-red-600"
                : "text-[var(--foreground-muted)]"
          }`}
        >
          {delta > 0 ? "+" : ""}
          {formatPercent(delta)}
        </span>
      </div>
      <div className="space-y-2.5">
        {barFor(baseline, "bg-[var(--foreground-muted)]/60", "Baseline")}
        {barFor(candAcc, better ? "bg-green-500" : "bg-amber-500", "Candidate")}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-1 text-center text-xs">
        <Metric
          label="Fixed"
          value={candidate.fixed_scenarios.length}
          tone="success"
        />
        <Metric
          label="Remaining"
          value={candidate.remaining_failures.length}
          tone={candidate.remaining_failures.length > 0 ? "warning" : "muted"}
        />
        <Metric
          label="Regressions"
          value={candidate.new_regressions.length}
          tone={candidate.new_regressions.length > 0 ? "danger" : "muted"}
        />
        <Metric
          label="Critical"
          value={candidate.critical_failures.length}
          tone={candidate.critical_failures.length > 0 ? "critical" : "safe"}
          help={
            candidate.critical_failures.length > 0
              ? "Blocks prompt acceptance"
              : "No acceptance-blocking failures"
          }
        />
      </div>
    </div>
  );
};

const Metric: React.FC<{
  label: string;
  value: number;
  tone: "success" | "warning" | "danger" | "muted" | "safe" | "critical";
  help?: string;
}> = ({ label, value, tone, help }) => {
  const color =
    tone === "success"
      ? "text-green-600"
      : tone === "safe"
        ? "text-green-700"
      : tone === "warning"
        ? "text-amber-600"
        : tone === "danger" || tone === "critical"
          ? "text-red-600"
          : "text-[var(--foreground-muted)]";
  const frame =
    tone === "critical"
      ? "border-red-300 bg-red-50"
      : tone === "safe"
        ? "border-green-200 bg-green-50"
        : "border-[var(--border)] bg-[var(--card)]";
  return (
    <div className={`rounded-md border py-1.5 px-2 ${frame}`}>
      <div className={`text-base font-bold tabular-nums ${color}`}>{value}</div>
      <div
        className={`text-[10px] uppercase tracking-wide ${
          tone === "critical"
            ? "text-red-700"
            : tone === "safe"
              ? "text-green-800"
              : "text-[var(--foreground-muted)]"
        }`}
      >
        {label}
      </div>
      {help && (
        <div className="mt-0.5 text-[10px] leading-tight text-[var(--foreground-muted)] normal-case">
          {help}
        </div>
      )}
    </div>
  );
};

const PromptDiff: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => {
  const candidate = lab.activeCandidate!;
  const currentPrompt = lab.currentPrompt || candidate.current_prompt;
  const [showFullPrompt, setShowFullPrompt] = useState(false);
  const [renderMarkdown, setRenderMarkdown] = useState(true);
  const diff = useMemo(
    () => buildPromptDiff(currentPrompt, candidate.proposed_prompt),
    [currentPrompt, candidate.proposed_prompt]
  );
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">Prompt diff</h4>
          <p className="text-xs text-[var(--foreground-muted)] mt-0.5">
            {showFullPrompt
              ? "Full prompt"
              : `${diff.changedLineCount} changed lines · unchanged prompt collapsed`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setRenderMarkdown((current) => !current)}
          className="text-xs text-[var(--accent)] hover:underline"
        >
          {renderMarkdown ? "Show raw" : "Render markdown"}
        </button>
        <button
          type="button"
          onClick={() => setShowFullPrompt((current) => !current)}
          className="text-xs text-[var(--accent)] hover:underline ml-3"
        >
          {showFullPrompt ? "Show changes only" : "Show full prompt"}
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {showFullPrompt ? (
          <>
            <PromptColumn
              title="Current"
              tone="muted"
              content={currentPrompt}
              renderMarkdown={renderMarkdown}
            />
            <PromptColumn
              title="Proposed"
              tone="accent"
              content={candidate.proposed_prompt}
              renderMarkdown={renderMarkdown}
            />
          </>
        ) : (
          <>
            <PromptColumn
              title="Current changes"
              tone="muted"
              content={diff.currentChanged || "(no current lines changed)"}
              lineLabel={diff.currentLineLabel}
              renderMarkdown={renderMarkdown}
            />
            <PromptColumn
              title="Proposed changes"
              tone="accent"
              content={diff.proposedChanged || "(no proposed lines changed)"}
              lineLabel={diff.proposedLineLabel}
              renderMarkdown={renderMarkdown}
            />
          </>
        )}
      </div>
    </section>
  );
};

const buildPromptDiff = (currentPrompt: string, proposedPrompt: string) => {
  const currentLines = currentPrompt.split("\n");
  const proposedLines = proposedPrompt.split("\n");
  let prefix = 0;
  while (
    prefix < currentLines.length &&
    prefix < proposedLines.length &&
    currentLines[prefix] === proposedLines[prefix]
  ) {
    prefix += 1;
  }

  let suffix = 0;
  while (
    suffix < currentLines.length - prefix &&
    suffix < proposedLines.length - prefix &&
    currentLines[currentLines.length - 1 - suffix] ===
      proposedLines[proposedLines.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const currentChanged = currentLines.slice(prefix, currentLines.length - suffix);
  const proposedChanged = proposedLines.slice(prefix, proposedLines.length - suffix);
  const lineLabel = (start: number, count: number) =>
    count === 0
      ? "0 lines"
      : count === 1
        ? `line ${start + 1}`
        : `lines ${start + 1}-${start + count}`;

  return {
    currentChanged: currentChanged.join("\n"),
    proposedChanged: proposedChanged.join("\n"),
    currentLineLabel: lineLabel(prefix, currentChanged.length),
    proposedLineLabel: lineLabel(prefix, proposedChanged.length),
    changedLineCount: Math.max(currentChanged.length, proposedChanged.length),
  };
};

const PromptColumn: React.FC<{
  title: string;
  tone: "muted" | "accent";
  content: string;
  lineLabel?: string;
  renderMarkdown: boolean;
}> = ({ title, tone, content, lineLabel, renderMarkdown }) => (
  <div
    className={`rounded-md border ${
      tone === "accent"
        ? "border-[var(--accent)]/30 bg-[var(--accent)]/5"
        : "border-[var(--border)] bg-[var(--card-container)]"
    }`}
  >
    <div className="px-3 py-2 border-b border-[var(--border)] flex items-center justify-between text-xs">
      <span className="font-semibold uppercase tracking-wide">{title}</span>
      <span className="text-[var(--foreground-muted)] tabular-nums">
        {lineLabel ?? `${content.split("\n").length} lines`}
      </span>
    </div>
    <div className="px-3 py-2 max-h-[280px] overflow-auto text-xs [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
      {renderMarkdown ? (
        <MarkdownContent emptyText="No prompt content.">
          {content || ""}
        </MarkdownContent>
      ) : (
        <pre className="font-mono whitespace-pre-wrap break-words text-[var(--foreground)]">
          {content || "(empty)"}
        </pre>
      )}
    </div>
  </div>
);

const ImpactGroups: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => {
  const candidate = lab.activeCandidate!;
  const baselineRun = lab.runs.find(
    (run) => run.id === (candidate.baseline_run_id || candidate.source_run_id)
  );
  const candidateRun = lab.runs.find(
    (run) => candidate.comparison_run_id && run.id === candidate.comparison_run_id
  );
  const baselineResults = new Map(
    (baselineRun?.results ?? []).map((result) => [result.scenario_id, result])
  );
  const candidateResults = new Map(
    (candidateRun?.results ?? []).map((result) => [result.scenario_id, result])
  );
  const groups: {
    title: string;
    ids: string[];
    tone: "good" | "bad" | "danger" | "safe";
    description: string;
    emptyText: string;
  }[] = [
    {
      title: "Fixed",
      ids: candidate.fixed_scenarios,
      tone: "good",
      description: "Failed in the baseline, passed with this candidate.",
      emptyText: "No failures were fixed by this candidate.",
    },
    {
      title: "Still failing",
      ids: candidate.remaining_failures,
      tone: "bad",
      description: "Still does not meet the saved scenario pass criteria.",
      emptyText: "No remaining failures.",
    },
    {
      title: "Regressions",
      ids: candidate.new_regressions,
      tone: "danger",
      description: "Passed before, failed after the proposed prompt.",
      emptyText: "No new regressions.",
    },
    {
      title: "Critical",
      ids: candidate.critical_failures,
      tone: candidate.critical_failures.length > 0 ? "danger" : "safe",
      description: "Blocks acceptance because this is unsafe, non-compliant, or a severe behavior miss.",
      emptyText: "No critical failures. Candidate is not blocked by critical impact.",
    },
  ];
  const visible = groups.filter((group) => group.ids.length > 0 || group.title === "Critical");

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-medium">Scenario impact</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {visible.map((group) => (
          <div
            key={group.title}
            className="rounded-md border border-[var(--border)] bg-[var(--card-container)] p-3 space-y-1.5"
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide">
                  {group.title}
                </span>
                <p className="text-[11px] text-[var(--foreground-muted)] mt-0.5">
                  {group.description}
                </p>
              </div>
              <span
                className={`text-xs font-semibold tabular-nums ${
                  group.tone === "good"
                    ? "text-green-600"
                    : group.tone === "safe"
                      ? "text-green-700"
                      : group.tone === "danger"
                      ? "text-red-600"
                      : "text-amber-600"
                }`}
              >
                {group.ids.length}
              </span>
            </div>
            {group.ids.length === 0 ? (
              <div
                className={`rounded-md border px-3 py-2 text-xs ${
                  group.tone === "safe"
                    ? "border-green-200 bg-green-50 text-green-800"
                    : "border-[var(--border)] bg-[var(--card)] text-[var(--foreground-muted)]"
                }`}
              >
                {group.emptyText}
              </div>
            ) : (
            <ul className="space-y-2 max-h-[360px] overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {group.ids.map((scenarioId) => {
                const scenario = lab.scenarios.find(
                  (item) => item.id === scenarioId
                );
                const baseline = baselineResults.get(scenarioId);
                const candidateResult = candidateResults.get(scenarioId);
                return (
                  <li key={scenarioId}>
                    <button
                      type="button"
                      onClick={() => {
                        lab.setSelectedScenarioId(scenarioId);
                        lab.setActiveTab("scenarios");
                        lab.setEditorSubTab("verdict");
                      }}
                      className="w-full text-left rounded-md border border-transparent hover:border-[var(--border)] hover:bg-[var(--card)] p-2"
                    >
                      <span className="block text-xs font-medium text-[var(--foreground)]">
                        {scenario?.title ?? shortId(scenarioId)}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-[var(--foreground-muted)]">
                        <VerdictPill label="Baseline" result={baseline} />
                        <span>→</span>
                        <VerdictPill label="Candidate" result={candidateResult} />
                        {scenario?.persona_type && (
                          <span className="capitalize">
                            {scenario.persona_type}
                          </span>
                        )}
                      </span>
                      <ScenarioResultReason result={candidateResult ?? baseline} />
                    </button>
                  </li>
                );
              })}
            </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

const VerdictPill: React.FC<{
  label: string;
  result?: AgentEvaluationScenarioResult;
}> = ({ label, result }) => {
  const verdict = result?.verdict ?? "pending";
  const tone =
    result?.critical_failure || verdict === "fail"
      ? "bg-red-50 text-red-700 border-red-200"
      : verdict === "pass"
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <span className={`rounded-full border px-1.5 py-0.5 ${tone}`}>
      {label}: {verdict}
      {typeof result?.score === "number" ? ` ${formatPercent(result.score)}` : ""}
    </span>
  );
};

const ScenarioResultReason: React.FC<{
  result?: AgentEvaluationScenarioResult;
}> = ({ result }) => {
  const reason =
    result?.failure_reason ||
    result?.judge_notes ||
    result?.fixes_needed?.[0] ||
    "";
  if (!reason) return null;
  return (
    <span className="block mt-1 text-[11px] leading-snug text-[var(--foreground-muted)] line-clamp-2">
      {reason}
    </span>
  );
};

const PromptHistory: React.FC<{ lab: UseAgentTestLab }> = ({ lab }) => (
  <div className="space-y-4">
    <div>
      <h4 className="text-sm font-medium mb-2">Prompt versions</h4>
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {lab.promptVersions.length === 0 ? (
          <div className="text-xs text-[var(--foreground-muted)] italic">
            No accepted versions yet.
          </div>
        ) : (
          lab.promptVersions.map((version) => (
            <div
              key={version.id}
              className="border border-[var(--border)] rounded-md p-2.5 bg-[var(--card)]"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium capitalize">
                    {version.source}
                  </div>
                  <div className="text-[10px] text-[var(--foreground-muted)]">
                    {new Date(version.created_at).toLocaleString()}
                  </div>
                  <div className="mt-1 text-[10px] text-[var(--foreground-muted)] truncate">
                    {shortId(version.id)}
                    {version.candidate_id ? ` · candidate ${shortId(version.candidate_id)}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => lab.handleRollback(version)}
                  disabled={lab.promptActionInFlight}
                  className="text-xs text-[var(--accent)] hover:underline disabled:opacity-50"
                >
                  Rollback
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>

    <div>
      <h4 className="text-sm font-medium mb-2">Candidate history</h4>
      <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {lab.promptCandidates.length === 0 ? (
          <div className="text-xs text-[var(--foreground-muted)] italic">
            No candidates yet.
          </div>
        ) : (
          lab.promptCandidates.map((candidate) => (
            <div
              key={candidate.id}
              className="border border-[var(--border)] rounded-md p-2.5 bg-[var(--card)]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium tabular-nums">
                  {shortId(candidate.id)}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded-full border text-[10px] capitalize ${getStatusClasses(
                    candidate.status
                  )}`}
                >
                  {statusLabel(candidate.status)}
                </span>
              </div>
              <div className="text-[10px] text-[var(--foreground-muted)] mt-1.5 tabular-nums flex flex-wrap gap-x-2 gap-y-1">
                <span>
                  {formatPercent(candidate.baseline_accuracy)} →{" "}
                  {formatPercent(candidate.candidate_accuracy)}
                </span>
                <span>{candidate.source}</span>
                <span>iteration {candidate.iteration_count}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
);
